import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Sparkles, 
  ArrowRight, 
  Shield, 
  CheckCircle, 
  Clock,
  Search,
  Link2,
  Target,
  CalendarDays,
  BarChart3,
  Bot,
  MessageCircle,
  FileCode,
  Languages,
  Zap,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Play,
  Globe,
  Users,
  Cpu,
  TrendingUp
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Import iOS 26 Design System
import '@/styles/ios26-design-system.css';

/**
 * Landing Page - Progressive Disclosure Design
 * =============================================
 * SIMPLE by default + DETAILED on click
 * - Hero: Clear title + short subtitle + 1 CTA
 * - 3 Simple Steps (white cards, minimal text)
 * - "En savoir plus" toggle → reveals detailed sections
 */
export default function LandingPageV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const detailsRef = useRef(null);
  const simpleStepsRef = useRef(null);
  
  // Handle URL hash for deep linking
  useEffect(() => {
    if (location.hash === '#details') {
      setIsDetailsOpen(true);
      // Scroll to details after a short delay
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);
  
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  
  const toggleDetails = () => {
    const newState = !isDetailsOpen;
    setIsDetailsOpen(newState);
    
    // Scroll to show details when opening
    if (newState) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };
  
  // Scroll to simple steps (for nav link)
  const scrollToHowItWorks = () => {
    simpleStepsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--color-bg))' }}>
      {/* ============================================
          TOP NAVIGATION - DARK MODE COMPATIBLE
          ============================================ */}
      <header 
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{ 
          backgroundColor: 'hsl(var(--color-surface) / 0.85)',
          borderBottom: '1px solid hsl(var(--color-border))'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-semantic">AI Marketing Engine</span>
            </div>
            
            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={scrollToHowItWorks}
                className="text-semantic-2 hover:text-semantic transition-colors"
              >
                Comment ça marche
              </button>
              <a 
                href="#pricing" 
                className="text-semantic-2 hover:text-semantic transition-colors"
              >
                Tarifs
              </a>
              <button 
                onClick={() => navigate('/auth')}
                className="text-semantic-2 hover:text-semantic transition-colors"
              >
                Connexion
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* ============================================
          HERO SECTION - Simple & Clean - DARK MODE COMPATIBLE
          ============================================ */}
      <section 
        className="pt-16 pb-20 px-6"
        style={{ 
          background: 'linear-gradient(to bottom, hsl(var(--color-info-bg) / 0.3), hsl(var(--color-bg)))'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-8"
            style={{ 
              backgroundColor: 'hsl(var(--color-info-bg))',
              color: 'hsl(var(--color-info-text))'
            }}
          >
            <Zap className="w-4 h-4" />
            Marketing automatisé par IA
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-semantic leading-tight mb-6">
            Votre marketing en pilote automatique
          </h1>
          
          {/* Subheadline - Short */}
          <p className="text-lg md:text-xl text-semantic-2 max-w-2xl mx-auto mb-10">
            L'IA crée et publie vos contenus automatiquement. Vous validez, elle s'occupe du reste.
          </p>
          
          {/* Single Primary CTA */}
          <button 
            onClick={() => navigate('/pricing')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-semibold rounded-full shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-all text-lg"
          >
            Commencer gratuitement
            <ArrowRight className="w-5 h-5" />
          </button>
          
          {/* Trust Badges - Minimal */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-semantic-muted">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-success))' }} />
              <span>14 jours d'essai gratuit</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-success))' }} />
              <span>Sans carte bancaire</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SIMPLE 3-STEP SECTION (Default View) - DARK MODE COMPATIBLE
          ============================================ */}
      <section 
        ref={simpleStepsRef}
        id="how-it-works" 
        className="py-20 px-6"
        style={{ backgroundColor: 'hsl(var(--color-surface))' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-semantic-2">
              En 3 étapes simples, votre marketing fonctionne en pilote automatique
            </p>
          </div>
          
          {/* 3 Simple Steps - Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div 
              className="rounded-2xl p-8 text-center"
              style={{ 
                backgroundColor: 'hsl(var(--color-surface-2))',
                border: '1px solid hsl(var(--color-border))',
                boxShadow: 'var(--ios-shadow-lg)'
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-6"
                style={{ backgroundColor: 'hsl(var(--color-primary))', color: 'white' }}
              >
                1
              </div>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Globe className="w-12 h-12" style={{ color: 'hsl(var(--color-primary))' }} />
              </div>
              <h3 className="text-xl font-bold text-semantic mb-3">
                Connectez votre site
              </h3>
              <p className="text-semantic-2">
                L'IA analyse automatiquement votre contenu, identifie vos thèmes clés et comprend votre audience cible.
              </p>
            </div>
            
            {/* Step 2 */}
            <div 
              className="rounded-2xl p-8 text-center"
              style={{ 
                backgroundColor: 'hsl(var(--color-surface-2))',
                border: '1px solid hsl(var(--color-border))',
                boxShadow: 'var(--ios-shadow-lg)'
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-6"
                style={{ backgroundColor: 'hsl(var(--color-primary))', color: 'white' }}
              >
                2
              </div>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Users className="w-12 h-12" style={{ color: 'hsl(var(--color-primary))' }} />
              </div>
              <h3 className="text-xl font-bold text-semantic mb-3">
                Connectez vos plateformes
              </h3>
              <p className="text-semantic-2">
                Instagram, Facebook, LinkedIn, X, Blog, Email... Reliez tous vos canaux en quelques clics.
              </p>
            </div>
            
            {/* Step 3 */}
            <div 
              className="rounded-2xl p-8 text-center"
              style={{ 
                backgroundColor: 'hsl(var(--color-surface-2))',
                border: '1px solid hsl(var(--color-border))',
                boxShadow: 'var(--ios-shadow-lg)'
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-6"
                style={{ backgroundColor: 'hsl(var(--color-primary))', color: 'white' }}
              >
                3
              </div>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Cpu className="w-12 h-12" style={{ color: 'hsl(var(--color-primary))' }} />
              </div>
              <h3 className="text-xl font-bold text-semantic mb-3">
                Le moteur travaille pour vous
              </h3>
              <p className="text-semantic-2">
                Création, planification, publication et optimisation automatique. Vous validez, l'IA s'occupe du reste.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          "EN SAVOIR PLUS" TOGGLE (The Bridge) - DARK MODE COMPATIBLE
          ============================================ */}
      <section 
        className="py-8 px-6"
        style={{ 
          backgroundColor: 'hsl(var(--color-surface-2))',
          borderTop: '1px solid hsl(var(--color-border))',
          borderBottom: '1px solid hsl(var(--color-border))'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <button
            onClick={toggleDetails}
            className="inline-flex flex-col items-center gap-2 group"
            data-testid="toggle-details-btn"
          >
            <span 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full shadow-md font-semibold transition-all group-hover:shadow-lg"
              style={{ 
                backgroundColor: 'hsl(var(--color-surface))',
                border: '1px solid hsl(var(--color-border))',
                color: 'hsl(var(--color-primary))'
              }}
            >
              {isDetailsOpen ? (
                <>
                  <ChevronUp className="w-5 h-5" />
                  Réduire
                </>
              ) : (
                <>
                  <ChevronDown className="w-5 h-5" />
                  En savoir plus
                </>
              )}
            </span>
            {!isDetailsOpen && (
              <span className="text-sm text-semantic-muted">
                Voir le détail des fonctionnalités et du fonctionnement
              </span>
            )}
          </button>
        </div>
      </section>

      {/* ============================================
          DETAILED CONTENT (Collapsed by default)
          ============================================ */}
      <div 
        ref={detailsRef}
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          isDetailsOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        )}
        data-testid="detailed-content"
      >
        {/* ----------------------------------------
            D1: 5 Guided Steps (Detailed)
            ---------------------------------------- */}
        <section className="py-20 px-6 ios-bg-mesh">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: 'hsl(var(--color-info-bg))', color: 'hsl(var(--color-info-text))' }}>
                <Bot className="w-4 h-4" />
                Guidé par l'IA
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-4">
                5 étapes simples pour automatiser
              </h2>
              <p className="text-lg text-semantic-2 max-w-2xl mx-auto">
                L'assistant IA vous accompagne à chaque étape de la configuration
              </p>
            </div>
            
            <div className="grid md:grid-cols-5 gap-6">
              {[
                { emoji: '🔍', title: 'Analyse du site', desc: 'L\'IA analyse votre contenu automatiquement' },
                { emoji: '🔌', title: 'Connexion des comptes', desc: 'Reliez vos réseaux sociaux (guidé par l\'IA)' },
                { emoji: '🎯', title: 'Création de campagne', desc: 'Définissez vos objectifs en 1 clic' },
                { emoji: '🗓', title: 'Génération 30 jours', desc: 'Un mois de contenu créé automatiquement' },
                { emoji: '📊', title: 'Résultats', desc: 'Suivez vos performances simplement' },
              ].map((step, i) => (
                <div 
                  key={i}
                  className="ios-glass-solid p-6 text-center"
                >
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl"
                    style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
                  >
                    {step.emoji}
                  </div>
                  <h3 className="font-semibold text-semantic mb-2">{step.title}</h3>
                  <p className="text-sm text-semantic-2">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ----------------------------------------
            D2: AI Assistant Section
            ---------------------------------------- */}
        <section className="py-20 px-6" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Text Content */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: 'hsl(var(--color-info-bg))', color: 'hsl(var(--color-info-text))' }}>
                  <Bot className="w-4 h-4" />
                  Nouveau
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-6">
                  Un assistant technique qui vous guide
                </h2>
                <p className="text-lg text-semantic-2 mb-8">
                  Vous n'avez pas besoin de comprendre OAuth, les API ou les tokens. 
                  Notre assistant IA lit les erreurs techniques et vous dit exactement quoi faire.
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: <MessageCircle className="w-5 h-5" />, title: 'Pas à pas, comme un tutoriel', desc: 'Chaque instruction vous dit où cliquer et quoi faire' },
                    { icon: <FileCode className="w-5 h-5" />, title: 'Analyse vos erreurs automatiquement', desc: 'Collez un message d\'erreur, l\'IA le traduit en français simple' },
                    { icon: <Languages className="w-5 h-5" />, title: 'Zéro jargon technique', desc: 'Toutes les explications sont en langage courant' },
                  ].map((feature, i) => (
                    <div key={i} className="flex gap-4 p-4 rounded-xl shadow-sm" style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(var(--color-info-bg))', color: 'hsl(var(--color-primary))' }}>
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-semantic">{feature.title}</h3>
                        <p className="text-sm text-semantic-2">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Visual - Chat Preview */}
              <div className="ios-glass p-2 rounded-3xl shadow-2xl">
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
                  <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <Bot className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold">Assistant technique IA</div>
                        <div className="text-sm opacity-80">En ligne • Répond instantanément</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-4 min-h-[280px]" style={{ backgroundColor: 'hsl(var(--chat-bg))' }}>
                    <div className="rounded-xl rounded-bl-md p-4 shadow-sm max-w-[85%]" style={{ backgroundColor: 'hsl(var(--chat-bubble-agent))', color: 'hsl(var(--chat-bubble-agent-text))' }}>
                      <p className="text-sm mb-3">
                        👋 Bonjour ! Je vois que vous avez une erreur de connexion Instagram. 
                        Pas de panique, c'est très simple à résoudre.
                      </p>
                      <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: 'hsl(var(--color-info-bg))', color: 'hsl(var(--color-info-text))' }}>
                        <p className="font-medium mb-2">📋 Voici ce qu'il faut faire :</p>
                        <ol className="space-y-1.5">
                          <li>1. Allez sur business.facebook.com</li>
                          <li>2. Cliquez sur "Paramètres" puis "Applications"</li>
                          <li>3. Autorisez AI Marketing Engine</li>
                        </ol>
                      </div>
                    </div>
                    <div className="rounded-xl rounded-br-md p-3 text-sm ml-auto max-w-[70%]" style={{ backgroundColor: 'hsl(var(--chat-bubble-user))', color: 'hsl(var(--chat-bubble-user-text))' }}>
                      Merci ! C'est fait ✅
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ----------------------------------------
            D3: Security & Control
            ---------------------------------------- */}
        <section className="py-20 px-6 ios-bg-gradient">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-4">
              Vous gardez le contrôle
            </h2>
            <p className="text-lg text-semantic-2 mb-12">
              Vos données sont protégées. Rien n'est publié sans votre accord.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <Shield className="w-6 h-6" />, title: 'Données chiffrées', desc: 'Vos tokens et accès sont cryptés et protégés' },
                { icon: <CheckCircle className="w-6 h-6" />, title: 'Validation avant publication', desc: 'Vous validez chaque contenu avant publication' },
                { icon: <FileCode className="w-6 h-6" />, title: 'Logs masqués automatiquement', desc: 'Les informations sensibles sont cachées par défaut' },
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-2xl" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'hsl(var(--color-success-bg))', color: 'hsl(var(--color-success))' }}>
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-semantic mb-2">{item.title}</h3>
                  <p className="text-sm text-semantic-2">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ----------------------------------------
            D4: Pricing Preview
            ---------------------------------------- */}
        <section id="pricing" className="py-20 px-6" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-4">
              Des tarifs simples
            </h2>
            <p className="text-lg text-semantic-2 mb-12">
              Commencez gratuitement pendant 14 jours. Sans carte bancaire.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                { name: 'Starter', price: '29€', period: '/mois', features: ['1 projet', '1 plateforme', '10 contenus/mois'], cta: 'Commencer' },
                { name: 'Pro', price: '79€', period: '/mois', features: ['3 projets', 'Toutes les plateformes', '50 contenus/mois'], cta: 'Commencer', popular: true },
                { name: 'Agency', price: '199€', period: '/mois', features: ['Projets illimités', 'Multi-clients', 'Support prioritaire'], cta: 'Contacter' },
              ].map((plan, i) => (
                <div 
                  key={i} 
                  className={cn('p-6 rounded-2xl text-left', plan.popular && 'scale-105')}
                  style={{
                    backgroundColor: plan.popular ? 'hsl(var(--pricing-card-highlight-bg))' : 'hsl(var(--pricing-card-bg))',
                    color: plan.popular ? 'white' : 'inherit',
                    boxShadow: plan.popular ? '0 25px 50px -12px hsl(var(--color-primary) / 0.35)' : 'var(--ios-shadow-lg)'
                  }}
                >
                  {plan.popular && (
                    <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'hsl(var(--pricing-highlight-muted))' }}>
                      Le plus populaire
                    </div>
                  )}
                  <h3 className="text-xl font-bold mb-2" style={{ color: plan.popular ? 'hsl(var(--pricing-highlight-title))' : 'hsl(var(--pricing-title-text))' }}>
                    {plan.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold" style={{ color: plan.popular ? 'hsl(var(--pricing-highlight-price))' : 'hsl(var(--pricing-price-text))' }}>
                      {plan.price}
                    </span>
                    <span style={{ color: plan.popular ? 'hsl(var(--pricing-highlight-muted))' : 'hsl(var(--pricing-muted-text))' }}>
                      {plan.period}
                    </span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm" style={{ color: plan.popular ? 'hsl(var(--pricing-highlight-feature))' : 'hsl(var(--pricing-feature-text))' }}>
                        <CheckCircle className="w-4 h-4" style={{ color: plan.popular ? 'hsl(var(--pricing-highlight-muted))' : 'hsl(var(--color-success))' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="w-full py-3 rounded-full font-semibold transition-all"
                    style={{
                      backgroundColor: plan.popular ? 'hsl(var(--pricing-highlight-button-bg))' : 'hsl(var(--pricing-button-primary-bg))',
                      color: plan.popular ? 'hsl(var(--pricing-highlight-button-text))' : 'hsl(var(--pricing-button-primary-text))'
                    }}
                  >
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => navigate('/pricing')}
              className="font-medium inline-flex items-center gap-1"
              style={{ color: 'hsl(var(--color-primary))' }}
            >
              Voir tous les détails
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* ----------------------------------------
            D5: FAQ (Accordion)
            ---------------------------------------- */}
        <section className="py-20 px-6 ios-bg-gradient">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-semantic mb-4 text-center">
              Questions fréquentes
            </h2>
            <p className="text-lg text-semantic-2 mb-12 text-center">
              Tout ce que vous devez savoir avant de commencer
            </p>
            
            <div className="space-y-3">
              {[
                { q: 'Est-ce que je dois être technique ?', a: 'Non, absolument pas. L\'assistant IA vous guide pas à pas pour chaque configuration. Vous n\'avez besoin d\'aucune connaissance technique.' },
                { q: 'Combien de temps pour configurer ?', a: '5 à 10 minutes en suivant l\'assistant. La plupart des utilisateurs finissent en moins de 10 minutes.' },
                { q: 'Et si ça ne marche pas ?', a: 'L\'assistant technique analyse automatiquement les erreurs et vous dit exactement quoi faire. Si vous êtes bloqué, notre support répond en moins de 24h.' },
                { q: 'Je peux arrêter quand je veux ?', a: 'Oui, sans engagement. Vous pouvez annuler votre abonnement à tout moment depuis les paramètres.' },
                { q: 'Vous publiez automatiquement sans mon accord ?', a: 'Non. Par défaut, chaque contenu vous est présenté pour validation. Vous pouvez activer la publication automatique si vous le souhaitez.' },
              ].map((faq, i) => (
                <div key={i} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
                  <button
                    onClick={() => toggleFaq(i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="font-semibold text-semantic">{faq.q}</span>
                    <ChevronDown className={cn('w-5 h-5 text-semantic-muted transition-transform', openFaq === i && 'rotate-180')} />
                  </button>
                  <div className={cn('overflow-hidden transition-all duration-300', openFaq === i ? 'max-h-40 pb-5' : 'max-h-0')}>
                    <div className="px-5">
                      <p className="text-semantic-2">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ============================================
          "FINI LES PROBLÈMES" SECTION (Always visible) - DARK MODE COMPATIBLE
          Simple pain points cards
          ============================================ */}
      <section 
        className="py-20 px-6"
        style={{ backgroundColor: 'hsl(var(--color-surface))' }}
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-semantic text-center mb-12">
            Fini les problèmes de marketing
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: <Clock className="w-6 h-6" />, problem: '"Je n\'ai pas le temps de créer du contenu"', desc: 'Publier régulièrement demande des heures chaque semaine.', solution: 'L\'IA génère vos contenus automatiquement' },
              { icon: <Target className="w-6 h-6" />, problem: '"Je ne sais pas quoi publier"', desc: 'Trouver des idées pertinentes est épuisant.', solution: 'L\'IA analyse votre site et propose des thèmes' },
              { icon: <TrendingUp className="w-6 h-6" />, problem: '"La publicité coûte trop cher"', desc: 'Les budgets pub explosent sans résultats garantis.', solution: 'Marketing organique à prix fixe' },
              { icon: <CalendarDays className="w-6 h-6" />, problem: '"Gérer plusieurs plateformes est un casse-tête"', desc: 'Jongler entre les réseaux sociaux prend trop de temps.', solution: 'Un seul tableau de bord pour tout gérer' },
            ].map((item, i) => (
              <div 
                key={i} 
                className="rounded-2xl p-6"
                style={{ 
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border))',
                  boxShadow: 'var(--ios-shadow-lg)'
                }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'hsl(var(--color-danger-bg))' }}
                  >
                    <span style={{ color: 'hsl(var(--color-danger))' }}>{item.icon}</span>
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-bold mb-1"
                      style={{ color: 'hsl(var(--color-danger))' }}
                    >
                      {item.problem}
                    </h3>
                    <p className="text-semantic-2 text-sm mb-3">{item.desc}</p>
                    <div 
                      className="flex items-center gap-2 text-sm font-medium"
                      style={{ color: 'hsl(var(--color-success))' }}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {item.solution}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA
          ============================================ */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à automatiser votre marketing ?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Rejoignez les entrepreneurs qui gagnent du temps grâce à l'IA. 
            Configuration en 5 minutes, résultats immédiats.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-full shadow-lg hover:bg-blue-50 transition-all text-lg"
            >
              Commencer gratuitement
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================
          FOOTER
          ============================================ */}
      <footer className="bg-gray-900 text-gray-400 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 text-white mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <span className="font-bold">AI Marketing Engine</span>
              </div>
              <p className="text-sm">
                Automatisation marketing IA pour indépendants et TPE.
              </p>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">Produit</h4>
              <ul className="space-y-3 text-sm">
                <li><button onClick={scrollToHowItWorks} className="hover:text-white transition-colors">Comment ça marche</button></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Tarifs</a></li>
                <li><a href="/auth" className="hover:text-white transition-colors">Connexion</a></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Légal</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Politique de confidentialité</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Conditions d'utilisation</a></li>
                <li><a href="/data-deletion" className="hover:text-white transition-colors">Suppression des données</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="mailto:support@ai-marketing-engine.com" className="hover:text-white transition-colors">support@ai-marketing-engine.com</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© 2025 AI Marketing Engine. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
